import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import dateFormat from "dateformat";
import { Dropdown } from "react-bootstrap";
import moment from "moment";
import Tooltip from "src/components/tooltip";
import InfiniteScroll from "src/components/infiniteScroll";
import Thumbnail from "src/components/thumbnail";
import { FileService } from "src/utils/file.service";
import { AddLearnersDialog } from "./add";
import { LearnerSearchBar } from "./filters/LearnerSearchBar";
import { StatusFilter, LearnerStatusOption } from "./filters/StatusFilter";
import { UserGroupsFilter } from "./filters/UserGroupsFilter";
import filterStyles from "./filters/filters.module.scss";

import { Course } from "src/types/coaching/courses";
import { Podcast } from "src/types/coaching/podcast";
import { Simulation } from "src/types/coaching/simulation";
import { FetchCourseLearnersRequest } from "src/types/coaching/learners";
import { UserGroup } from "src/types/users";
import { getDueText } from "src/pages/coaching/components/course/view";
import {
  getLearnerNavigationState,
  getLearnerViewFromPath,
  getManageNavigationFrom,
  getManageNavigationRootFrom,
  getManageNavigationSelectedUserGroups,
  getManageNavigationState,
  getPublishedOverviewPath,
  isPublishedOverviewPath,
  type CoachingEntityType,
} from "src/pages/coaching/utils/manageNavigation";

import { flattenContents } from "../../helper";

import { fetchCourseLearners } from "src/redux/actions/coaching/learners";

import _ from "lodash";
import cx from "classnames";
import styles from "./learners.module.scss";

interface CourseLearnersProps {
  id: string;
  course: Course | Podcast | Simulation;
  hideAddLearners?: boolean;
  navigationFrom?: string;
}

export const CourseLearners: React.FC<CourseLearnersProps> = ({
  id,
  course,
  hideAddLearners = false,
  navigationFrom,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const { loading, learners, totalRecordsCount, currentPage, nextPage, hasMoreItems } = useSelector(
    (state: any) => {
      const learnersState = _.get(state, "courses.learners");
      const loading = _.get(learnersState, "loading");
      const pagination = _.get(learnersState, "pagination");
      const totalRecordsCount = _.get(pagination, "totalRecordsCount", 0);
      const currentPage = _.get(pagination, "currentPage", 0);
      const total = _.get(pagination, "totalPages", 0);
      const hasMoreItems = _.get(pagination, "hasMoreItems");

      const learners = _.get(learnersState, "items") || [];

      let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
      if (nextPage && nextPage >= total) {
        nextPage = null;
      }

      return {
        loading: loading,
        learners: learners,
        totalRecordsCount: totalRecordsCount,
        currentPage: currentPage,
        nextPage: nextPage,
        hasMoreItems: hasMoreItems,
      };
    },
    _.isEqual,
  );

  const [showAddLearner, setShowAddLearner] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUserGroups, setSelectedUserGroups] = useState<UserGroup[]>(
    () => getManageNavigationSelectedUserGroups(location.state) ?? [],
  );
  const [selectedStatus, setSelectedStatus] = useState<LearnerStatusOption>({
    value: "ALL",
    label: "All learners",
  });

  const statusOptions = useMemo(
    () =>
      [
        { label: "All learners", value: "ALL" },
        { label: "Completed", value: "COMPLETED" },
        { label: "In progress", value: "IN_PROGRESS", show: course?.entityType !== "simulation" },
        { label: "Not started", value: "NOT_STARTED" },
        { label: "Overdue", value: "OVERDUE" },
      ].filter((option) => option.show !== false),
    [course?.entityType],
  );

  const buildRequest = useCallback(
    (page: number): FetchCourseLearnersRequest => ({
      course: id,
      status: selectedStatus.value,
      page,
      limit: 10,
      query: searchQuery || undefined,
      userGroups: selectedUserGroups.length
        ? selectedUserGroups.map((group) => group.id)
        : undefined,
    }),
    [id, searchQuery, selectedStatus.value, selectedUserGroups],
  );

  const fetchLearners = useCallback(
    (page: number) => {
      if (id) {
        dispatch(fetchCourseLearners(buildRequest(page)));
      }
    },
    [buildRequest, id],
  );

  useEffect(() => {
    fetchLearners(0);
  }, [fetchLearners]);

  const handleStatusChange = (option: LearnerStatusOption) => {
    setSelectedStatus(option);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleUserGroupsChange = (groups: UserGroup[]) => {
    setSelectedUserGroups(groups);
  };

  const handleLoadMoreLearners = () => {
    if (nextPage !== null) {
      fetchLearners(nextPage);
    }
  };

  const handleAddLearnersFinished = () => {
    setShowAddLearner(false);
    fetchLearners(0);
  };

  const showEmptyState = _.size(learners) === 0 && !loading;

  return (
    <div className={styles.learnersContainer}>
      <div className={styles.headerContainer}>
        <div className={styles.header}>
          <span className={styles.title}>
            {selectedStatus.label} ({totalRecordsCount})
          </span>
        </div>
          <div
            className={styles.addLearner}
            onClick={() => setShowAddLearner(true)}
          >
            <i className="paperflite-add-assignee" />
            <span>add learners</span>
          </div>
      </div>


      {/* <LearnerSearchBar query={searchQuery} onSearch={handleSearch} /> */}

      <div className={styles.filtersRow}>
        <StatusFilter
          selected={selectedStatus}
          options={statusOptions}
          onChange={handleStatusChange}
        />
        <UserGroupsFilter
          selectedGroups={selectedUserGroups}
          onChange={handleUserGroupsChange}
          className={filterStyles.alignRight}
        />
      </div>

      <div className={styles.stacks}>
        <InfiniteScroll
          className={styles.scroll}
          loading={loading}
          hasMore={hasMoreItems}
          loadMore={handleLoadMoreLearners}
          loader={() => {
            return _.times(_.size(learners) === 0 || currentPage === 0 ? 2 : 1, (index) => (
              <LearnerPlaceholder key={index} />
            ));
          }}
        >
          {_.size(learners) === 0 && !loading && (
            <span className={styles.noRecordsFound}>No learners.</span>
          )}
          {/* {showEmptyState && (
            <div className={styles.emptyState} role="status">
              <LearnerPlaceholder disableShimmer showBlocks />

              <div className={styles.emptyRow}>
                <div className={styles.emptyAvatar} />
                {selectedUserGroups.length > 0 ? (
                  <span className={styles.emptyText}>no learners found</span>
                ) : (
                  <span className={styles.emptyText}>no learners added yet!</span>
                )}
              </div>

              {selectedUserGroups.length > 0 && (
                <LearnerPlaceholder disableShimmer showBlocks />
              )}

              {!selectedUserGroups.length && !hideAddLearners && (
                <div className={styles.emptyRow}>
                  <div className={styles.emptyAvatar} />
                  <button
                    type="button"
                    className={styles.addLearnersRow}
                    onClick={() => setShowAddLearner(true)}
                  >
                    <i className="paperflite-add-assignee" />
                    <span>add learners</span>
                  </button>
                </div>
              )}
            </div>
          )} */}
          <div className={styles.learnersList}>
            {learners.map((learner) => (
              <LearnerRow
                key={learner?.user?.id || learner?.id}
                learner={learner}
                course={course}
                navigationFrom={navigationFrom}
                selectedUserGroups={selectedUserGroups}
              />
            ))}
          </div>
        </InfiniteScroll>
      </div>

      {showAddLearner && !hideAddLearners && (
        <AddLearnersDialog
          show={showAddLearner}
          onFinish={handleAddLearnersFinished}
          onClose={() => setShowAddLearner(false)}
        />
      )}
    </div>
  );
};

interface LearnerRowProps {
  learner: any;
  course: any;
  navigationFrom?: string;
  selectedUserGroups?: UserGroup[];
}

const LearnerRow: React.FC<LearnerRowProps> = ({
  learner,
  course,
  navigationFrom,
  selectedUserGroups = [],
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const name = useMemo(() => {
    return `${learner.user.firstName || ""} ${learner.user.lastName || ""}`;
  }, [learner]);

  const entityType = useMemo(() => {
    return _.get(course, "entityType");
  }, [course]);

  const score = Math.round(learner?.score * 100);

  const passed = useMemo(() => {
    let pass = learner.status === "COMPLETED";

    if (entityType === "course") {
      pass = score >= course.passPercentage;
    }
    return pass;
  }, [learner, entityType, course, score]);

  const dueDate = useMemo(() => {
    const date = _.get(learner, "dueDate");
    return date ? new Date(date) : null;
  }, [learner]);

  const isOverdue = dueDate && moment(dueDate).isBefore(moment(), "day");

  const dueText = getDueText(dueDate, learner.status);

  const showDueTimer =
    (learner.status === "IN_PROGRESS" && learner.dueDate) ||
    ((learner.status === "COMPLETED" || learner.status === "NOT_STARTED") && isOverdue);

  const getProgressContent = (learner) => {
    const score = Math.round(learner?.score * 100);
    if (learner.status === "IN_PROGRESS" && entityType === "podcast") {
      return FileService.formatDuration(learner.duration, "short");
    }

    if (learner.status === "COMPLETED") {
      switch (entityType) {
        case "simulation": {
          if (score === 0) {
            return "na";
          }
          return `${score}%`;
        }

        case "podcast":
          return FileService.formatDuration(learner.duration, "short");

        case "course": {
          const allContents = flattenContents(course?.contents || []);
          const assessment = allContents?.some(
            (c) => c.entityType === "assessment" || c.entityType === "simulation",
          );

          if (!assessment) {
            return null;
          }
          return (
            <span
              className={cx(styles.score, {
                [styles.pass]: passed,
                [styles.fail]: !passed,
              })}
            >
              {`${score}%`}
            </span>
          );
        }
      }
    }
  };

  const handleNavigate = (learnerId: string) => {
    if (learner.status !== "COMPLETED") return;

    const { entityType, id } = course;

    const reportPaths: Record<string, string> = {
      simulation: `/coaching/manage/simulations/${id}/reports/${learnerId}`,
      course: `/coaching/manage/courses/${id}/reports/${learnerId}`,
    };

    const path = reportPaths[entityType];
    if (!path) return;
    
    const previousNavigation = getManageNavigationState(location.state);
    const listOrigin = getManageNavigationRootFrom(location.state);
    const parentFrom = navigationFrom ?? previousNavigation.from ?? getManageNavigationFrom(location.state);
    const isDraftPage = location.pathname.includes("/draft/");
    const publishedOverviewPath = getPublishedOverviewPath(
      entityType as CoachingEntityType,
      id,
    );
    
    const returnTo = (() => {
      if (isDraftPage) return location.pathname;
      if (previousNavigation.returnTo) return previousNavigation.returnTo;
      return undefined;
    })();
    
    const from = isDraftPage
      ? (isPublishedOverviewPath(previousNavigation.from ?? "")
          ? previousNavigation.from!
          : publishedOverviewPath)
      : (parentFrom ?? previousNavigation.from);
    
    const { mySpaceStatus } = getLearnerNavigationState(location.state);
    const learnerViewFrom = getLearnerViewFromPath(location.state);

    const navigationState = {
      ...(listOrigin && { rootFrom: listOrigin }),
      from,
      ...(returnTo && { returnTo }),
      ...(selectedUserGroups.length && { selectedUserGroups }),
      ...(mySpaceStatus && { mySpaceStatus }),
      ...(learnerViewFrom && { learnerViewFrom }),
    };
    
    navigate(path, { state: navigationState });
  }

  return (
    <div className={styles.learner} onClick={() => handleNavigate(learner.id)}>
      <div className={styles.details}>
        <div className={styles.avatar}>
          <Thumbnail
            media={learner.user?.icon}
            defaultImage="/images/assets/default_asset.jpg"
            className={styles.icon}
          />
          {showDueTimer && (
            <Tooltip content={dueText} placement="top">
              <i className={cx("paperflite-hour-glass", styles.dueIcon)} />
            </Tooltip>
          )}
        </div>
        <div className={styles.info}>
          <span className={styles.name}>
            {name}
            {passed && learner.status === "COMPLETED" && <i className="paperflite-flag" />}
            {!passed && learner.status === "COMPLETED" && <i className="paperflite-close" />}
          </span>
          <LearnerStatus learner={learner} course={course} />
        </div>
      </div>
      <div>
        <div className={styles.progress}>{getProgressContent(learner)}</div>
      </div>
    </div>
  );
};

export const LearnerStatus = ({ learner, course }) => {
  const entityType = _.get(course, "entityType");
  const assignedBy = _.get(learner, "assignedBy");
  const attempts = _.get(learner, "attempts", 0);
  const status = _.get(learner, "status");
  const dueDate = _.get(learner, "dueDate");

  const getLabel = () => {
    if (status === "NOT_STARTED") {
      if (dueDate) {
        return "due on";
      }
      return assignedBy ? "assigned on" : "enrolled on";
    }

    switch (entityType) {
      case "simulation":
        return "last call logged on";
      case "course":
        let label = "completed on";
        if (status === "IN_PROGRESS") {
          label = "started on";
        }

        if (attempts > 1) {
          label = `${attempts} attempts | ${label}`;
        }

        return label;

      case "podcast":
        return "last engaged on";

      default:
        return "";
    }
  };

  const getDate = () => {
    if (status === "NOT_STARTED" && dueDate) {
      return moment.utc(dueDate).format("DD MMM YYYY");
    }
    return dateFormat(learner.modifiedDate, "dd mmm yyyy");
  };

  return (
    <span className={styles.engagedDate}>
      {getLabel()} {getDate()}
    </span>
  );
};

export const LearnerPlaceholder: React.FC<{
  disableShimmer?: boolean;
  showBlocks?: boolean;
}> = ({ disableShimmer = false, showBlocks = true }) => {
  const shimmerClass = disableShimmer ? styles.static : styles.block;

  return (
    <div className={styles.learnerPlaceholder}>
      <div className={cx(styles.avatar, shimmerClass)} />
      {showBlocks && <div className={cx(styles.blockName, shimmerClass)} />}
    </div>
  );
};

type LearnerOption = {
  value: string;
  label: string;
};

interface LearnerTypeDropDownProps {
  selected: LearnerOption;
  options: LearnerOption[];
  onChange: (option: LearnerOption) => void;
}

export const LearnerTypeDropDown: React.FC<LearnerTypeDropDownProps> = ({
  selected,
  options,
  onChange,
}) => {
  const selectedOption = useMemo(
    () => options.find((option) => option.value === selected.value),
    [selected, options],
  );

  const handleSelect = (value: string) => {
    const option = options.find((option) => option.value === value);
    if (option) {
      onChange(option);
    }
  };

  return (
    <div className={styles.selectDropdown}>
      <Dropdown>
        <Dropdown.Toggle className={styles.button} as={"button"}>
          <span className={styles.filterList}></span>
        </Dropdown.Toggle>
        <Dropdown.Menu
          className={styles.dropdown}
          popperConfig={{
            modifiers: [
              {
                name: "offset",
                options: { offset: [0, 6] },
              },
            ],
          }}
        >
          {options.map((option, index) => (
            <Dropdown.Item
              key={index}
              className={cx(styles.dropdownItem, {
                [styles.active]: option.value === selectedOption?.value,
              })}
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};
